import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { GridRenderEditCellParams } from "@mui/x-data-grid";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useOptions } from "../../contexts/OptionsContext";
import { useSpinner } from "../../contexts/SpinnerContext";
import { Config, MinimalDataItem, RelatedEntityItem } from "../../types";
import { enqueueUniqueSnackbar } from "../../utilities/snackbarUtils";
import { extractRelationshipEntities } from "../../utilities/relationshipUtils";

interface MultiSelectDialogProps {
  params: GridRenderEditCellParams;
  options: MinimalDataItem[];
  onAddNew?: (onSuccess: (newItemId: number) => void) => void;
  category?: string;
  config: Config;
}

const BLOCKED_TABLES = ['family-areas', 'places'];

export const MultiSelectDialog = ({
  params,
  options = [],
  onAddNew,
  category,
  config,
}: MultiSelectDialogProps) => {
  const { loadOptionsForEndpoint, forceRefreshEndpoint } = useOptions();
  const { loading } = useSpinner();
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // More robust initialization of selected state
  const [selected, setSelected] = useState<number[]>(() => {
    try {
      let initialValues: number[] = [];
      const row = params.row;
      const endpoint = config.apiEndpoints.find((e) => e.name === category);

      if (!endpoint) return [];

      if (row?.relationships) {
        try {
          // Handle media entities
          if (endpoint.entityType === "media") {
            initialValues =
              row.relationships.media?.map(
                (item: RelatedEntityItem) => {
                  // Convert string IDs to numbers if possible (for Django compatibility)
                  if (typeof item.id === 'string') {
                    const numericId = parseInt(item.id, 10);
                    return isNaN(numericId) ? 0 : numericId; // Use 0 for non-numeric IDs (will be filtered out below)
                  }
                  return item.id;
                }
              ) || [];
          } else {
            // For all other entities, use utility function to handle backend inconsistency
            const relatedItems = extractRelationshipEntities(
              row.relationships, 
              endpoint.returnedRelationshipsKey
            );
            initialValues = relatedItems.map((item: RelatedEntityItem) => {
              // Convert string IDs to numbers if possible (for Django compatibility)
              if (typeof item.id === 'string') {
                const numericId = parseInt(item.id, 10);
                return isNaN(numericId) ? 0 : numericId; // Use 0 for non-numeric IDs (will be filtered out below)
              }
              return item.id;
            });
          }
        } catch (error) {
          enqueueUniqueSnackbar(
            enqueueSnackbar,
            "Error reading relationships",
            { variant: "error" }
          );
        }
      }

      if (initialValues.length === 0) {
        if (Array.isArray(params.value)) {
          initialValues = params.value
            .map(Number)
            .filter((id: number) => !isNaN(id));
        } else if (Array.isArray(row?.[params.field])) {
          initialValues = row[params.field]
            .map(Number)
            .filter((id: number) => !isNaN(id));
        }
      }

      return initialValues.filter(
        (id): id is number => typeof id === "number" && !isNaN(id) && id > 0
      );
    } catch (error) {
      enqueueUniqueSnackbar(enqueueSnackbar, "Error initializing selection", {
        variant: "error",
      });
      return [];
    }
  });

  const [searchTerm, setSearchTerm] = useState("");

  // Auto-load options when dialog opens if they don't exist
  useEffect(() => {
    if (open && (!options || options.length === 0) && category) {
      const endpoint = config.apiEndpoints.find((e) => e.name === category);
      if (endpoint) {
        console.log(`Auto-loading options for ${category} because they are empty`);
        loadOptionsForEndpoint(endpoint.tableName);
      }
    }
  }, [open, options, category, config.apiEndpoints, loadOptionsForEndpoint]);

  const filteredOptions = useMemo(() => {
    return options
      .filter((option) =>
        option?.name?.toLowerCase().includes(searchTerm?.toLowerCase() ?? "")
      )
      .sort((a, b) => (a?.name ?? "").localeCompare(b?.name ?? ""));
  }, [options, searchTerm]);

  const parentRef = React.useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => parentRef.current,
    estimateSize: useCallback(() => 48, []),
    overscan: 5,
    initialOffset: 0,
    initialRect: { width: 0, height: 400 },
  });

  useEffect(() => {
    rowVirtualizer.measure();
  }, [filteredOptions.length, rowVirtualizer]);

  useEffect(() => {
    if (parentRef.current) {
      window.dispatchEvent(new Event("resize"));
    }
  }, [filteredOptions.length]);

  // Handle refresh options
  const handleRefreshOptions = async () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    try {
      const endpoint = config.apiEndpoints.find((e) => e.name === category);
      if (endpoint) {
        await forceRefreshEndpoint(endpoint.tableName);
        enqueueSnackbar("Options refreshed successfully", { variant: "success" });
      }
    } catch (error) {
      console.error('Error refreshing options:', error);
      enqueueSnackbar("Failed to refresh options", { variant: "error" });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleToggleOption = (optionId: string | number) => {
    const numericId = Number(optionId);
    if (isNaN(numericId)) {
      enqueueUniqueSnackbar(enqueueSnackbar, "Invalid option ID", {
        variant: "error",
      });
      return;
    }
    setSelected((prev) =>
      prev.includes(numericId)
        ? prev.filter((id) => id !== numericId)
        : [...prev, numericId]
    );
  };

  const handleClose = () => {
    setOpen(false);
    params.api.stopCellEditMode({
      id: params.id,
      field: params.field,
      ignoreModifications: true,
    });
  };

  const handleSave = () => {
    try {
      const endpoint = config.apiEndpoints.find((e) => e.name === category);
      if (!endpoint) {
        enqueueUniqueSnackbar(enqueueSnackbar, "Configuration error", {
          variant: "error",
        });
        return;
      }

      const validIds = selected.filter(
        (id) => typeof id === "number" && !isNaN(id) && id > 0
      );

      const result = params.api.setEditCellValue(
        {
          id: params.id,
          field: params.field,
          value: validIds,
        },
        { force: true }
      );

      if (result instanceof Promise) {
        result
          .then(() => {
            params.api.stopCellEditMode({
              id: params.id,
              field: params.field,
            });
          })
          .catch(() => {
            enqueueUniqueSnackbar(enqueueSnackbar, "Error saving changes", {
              variant: "error",
            });
          });
      } else {
        params.api.stopCellEditMode({
          id: params.id,
          field: params.field,
        });
      }
      setOpen(false);
    } catch (error) {
      enqueueUniqueSnackbar(enqueueSnackbar, "Error saving changes", {
        variant: "error",
      });
    }
  };

  const handleAddNewClick = () => {
    if (!onAddNew || !category) {
      enqueueUniqueSnackbar(enqueueSnackbar, "Cannot add new item", {
        variant: "error",
      });
      return;
    }

    onAddNew(async (newItemId: number) => {
      if (!newItemId || isNaN(newItemId)) {
        enqueueUniqueSnackbar(enqueueSnackbar, "Invalid item ID received", {
          variant: "error",
        });
        return;
      }

      try {
        await loadOptionsForEndpoint(params.field);
        await new Promise((resolve) => setTimeout(resolve, 500));

        const newItem = options.find((opt) => opt.id === newItemId);
        if (!newItem) {
          throw new Error("New item not found in options after refresh");
        }

        setSelected((prev) => [...prev, newItemId]);

        const result = params.api.setEditCellValue(
          {
            id: params.id,
            field: params.field,
            value: [...selected, newItemId],
          },
          { force: true }
        );

        if (result instanceof Promise) {
          await result;
        }

        params.api.stopCellEditMode({
          id: params.id,
          field: params.field,
        });

        setOpen(false);
      } catch (error) {
        enqueueUniqueSnackbar(
          enqueueSnackbar,
          error instanceof Error ? error.message : "Error updating selection",
          { variant: "warning" }
        );
      }
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      keepMounted={false}
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {category ? `Select ${category}` : "Select Items"}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
            LOAD MORE
          </Typography>
          <Tooltip title="Refresh options - loads all available items">
            <IconButton 
              onClick={handleRefreshOptions} 
              size="small"
              disabled={isRefreshing || loading}
              sx={{
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.light',
                  color: 'primary.contrastText'
                },
                '&:disabled': {
                  color: 'action.disabled'
                }
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoFocus
            sx={{ mb: 1 }}
          />
        </Box>
        {loading || isRefreshing ? (
          <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", p: 3, gap: 2 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              {isRefreshing ? "Refreshing options..." : "Loading options..."}
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              ref={parentRef}
              style={{
                height: "400px",
                minHeight: "400px",
                overflow: "auto",
              }}
            >
              <div
                style={{
                  height: `${rowVirtualizer.getTotalSize()}px`,
                  width: "100%",
                  position: "relative",
                }}
              >
                {rowVirtualizer
                  .getVirtualItems()
                  .map(
                    (virtualRow: {
                      index: number;
                      start: number;
                      size: number;
                    }) => {
                      const option = filteredOptions[virtualRow.index];
                      const numericId = Number(option.id);
                      const isSelected = selected.includes(numericId);

                      return (
                        <ListItem
                          key={option.id}
                          disablePadding
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: `${virtualRow.size}px`,
                            transform: `translateY(${virtualRow.start}px)`,
                          }}
                        >
                          <ListItemButton
                            onClick={() => handleToggleOption(option.id)}
                          >
                            <ListItemIcon>
                              <Checkbox checked={isSelected} />
                            </ListItemIcon>
                            <ListItemText primary={option.name} />
                          </ListItemButton>
                        </ListItem>
                      );
                    }
                  )}
              </div>
            </Box>
            {onAddNew && (() => {
              const endpoint = config.apiEndpoints.find(e => e.name === category);
              // Only show Add New if not in blocked tables
              return endpoint && !BLOCKED_TABLES.includes(endpoint.tableName) ? (
                <>
                  <Divider />
                  <ListItem disablePadding>
                    <ListItemButton onClick={handleAddNewClick}>
                      <ListItemText primary={`+ Add New ${category}`} />
                    </ListItemButton>
                  </ListItem>
                </>
              ) : null;
            })()}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
